import { useMemo } from 'react';
import { merge } from 'lodash';

import useRestApi from 'app/common/hooks/useRestApi';

/**
 * Hook to fetch AuroraPay invoice PDF data for a customer order.
 *
 * @param {Object} config - Object containing additional request config such as
 *     the request params or request method
 * @param {String} orderId - The order ID
 * @param {String} invoiceRefId - The invoice reference ID (fulfillmentId or returnAuthId)
 * @param {Boolean} isReturns - Whether this is a return invoice (true) or regular invoice (false)
 * @param {Boolean} [sendImmediate=false] - Whether to send the request immediately upon
 *     using the hook. If false, then the caller is responsible for triggering
 *     the `sendCallback`. Defaults to `false`.
 * @return {{error: boolean, exception: Object, loading: boolean, response: Object, sendCallback: function, requestStatus: Object}}
 */
function useCustomerAuroraPayInvoice(
  config,
  orderId,
  invoiceRefId,
  isReturns = false,
  sendImmediate = false
) {
  // Determine the path based on isReturns flag
  const invoicePath = isReturns
    ? 'customer-return-invoice'
    : 'customer-invoice';
  const requestPath = `/order-operations/orders/${orderId}/${invoicePath}/${invoiceRefId}`;

  const finalConfig = useMemo(
    () =>
      merge(
        {},
        {
          method: 'get'
        },
        config
      ),
    [config]
  );

  return useRestApi(requestPath, finalConfig, sendImmediate);
}

export default useCustomerAuroraPayInvoice;
export { useCustomerAuroraPayInvoice };
